import React, { useState, useEffect } from "react";
import { db } from "../../firebase";
import {
  collection,
  getDocs,
  doc,
  updateDoc,
} from "firebase/firestore";

// Helper function to safely render Firestore Timestamps, strings, or numbers without crashing React
const formatFieldValue = (val) => {
  if (!val) return "";
  
  // If val is a Firestore Timestamp with .toDate()
  if (typeof val === "object" && typeof val.toDate === "function") {
    return val.toDate().toLocaleString();
  }
  
  // If val is a raw Firestore Timestamp object { seconds, nanoseconds }
  if (typeof val === "object" && val.seconds !== undefined) {
    return new Date(val.seconds * 1000).toLocaleString();
  }
  
  // Fallback for any other unexpected object to avoid React rendering error
  if (typeof val === "object") {
    return "";
  }
  
  return String(val);
};

export default function ShiftStatusManager() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  // Form State variables
  const [selectedShiftId, setSelectedShiftId] = useState("");
  const [clientName, setClientName] = useState("");
  const [staffName, setStaffName] = useState("");
  const [shiftDate, setShiftDate] = useState("");
  const [checkInTime, setCheckInTime] = useState("");
  const [checkOutTime, setCheckOutTime] = useState("");
  const [status, setStatus] = useState("Scheduled");

  // Fetch shifts on component mount
  const fetchShifts = async () => {
    setLoading(true);
    setError("");
    try {
      const snap = await getDocs(collection(db, "shifts"));
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      
      // Sort shifts by date descending safely
      list.sort((a, b) => {
        const dateB = formatFieldValue(b?.date || b?.checkOutDate || b?.createdAt);
        const dateA = formatFieldValue(a?.date || a?.checkOutDate || a?.createdAt);
        return dateB.localeCompare(dateA);
      });

      setShifts(list);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch shifts from Firestore.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShifts();
  }, []);

  // Populate the form fields whenever a shift is clicked from the table
  const handleSelectShift = (shift) => {
    setSelectedShiftId(shift.id);
    setClientName(shift.clientName || shift.client || "None");
    setStaffName(shift.staffName || shift.staff || "Unassigned");
    setShiftDate(formatFieldValue(shift.date));
    setCheckInTime(formatFieldValue(shift.checkInTime));
    setCheckOutTime(formatFieldValue(shift.checkOutTime));
    setStatus(shift.status || "Scheduled");
    setError("");
    setSuccessMsg("");
  };

  // Submit and save the updated times/status to Firestore
  const handleUpdateShiftStatus = async (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }

    if (!selectedShiftId) {
      setError("Please select a shift from the table below to edit first.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const shiftRef = doc(db, "shifts", selectedShiftId);
      
      const updatedFields = {
        checkInTime: checkInTime,
        checkOutTime: checkOutTime,
        status: status,
      };

      await updateDoc(shiftRef, updatedFields);
      setSuccessMsg(`Shift updated successfully!`);
      
      setSelectedShiftId("");
      setClientName("");
      setStaffName("");
      setShiftDate("");
      setCheckInTime("");
      setCheckOutTime("");
      setStatus("Scheduled");

      // Refresh local array
      await fetchShifts();
    } catch (err) {
      console.error(err);
      setError("Could not update shift metadata. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Inline Styles matching ShiftsEditor.js aesthetics
  const container = { maxWidth: 1150, margin: "24px auto", padding: "0 16px", fontFamily: "inherit" };
  const headerCard = { background: "linear-gradient(135deg, #111827 0%, #1f2937 100%)", padding: "24px 32px", borderRadius: 20, marginBottom: 24, boxShadow: "0 4px 20px rgba(0,0,0,0.08)" };
  const cardTitle = { fontSize: 24, fontWeight: 900, color: "#ffffff", margin: 0 };
  const cardSub = { color: "#9ca3af", fontSize: 14, marginTop: 4, fontWeight: 500 };
  
  const innerCard = { background: "#ffffff", borderRadius: 20, padding: 24, border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.02)", marginBottom: 24 };
  const labelStyle = { display: "block", fontSize: 13, fontWeight: 800, color: "#4b5563", marginBottom: 6 };
  const disabledInput = { width: "100%", padding: "11px 16px", borderRadius: 12, border: "1px solid #e5e7eb", background: "#f3f4f6", color: "#6b7280", boxSizing: "border-box", fontSize: 14, fontWeight: 600 };
  const activeInput = { width: "100%", padding: "11px 16px", borderRadius: 12, border: "1px solid #d1d5db", background: "#ffffff", color: "#111827", boxSizing: "border-box", fontSize: 14, fontWeight: 700, outline: "none" };
  
  const tableWrapper = { background: "#ffffff", borderRadius: 20, border: "1px solid #e5e7eb", overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.02)" };
  const scrollContainer = { overflowX: "auto" };
  const thStyle = { background: "#f9fafb", padding: "14px 16px", fontSize: 12, fontWeight: 800, color: "#4b5563", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "left", borderBottom: "1px solid #e5e7eb" };
  const tdStyle = { padding: "14px 16px", fontSize: 14, color: "#111827", fontWeight: 700, borderBottom: "1px solid #f3f4f6" };

  const btn = (bg) => ({
    background: bg,
    color: "#fff",
    border: "none",
    padding: "12px 24px",
    borderRadius: 12,
    fontWeight: 800,
    fontSize: 14,
    cursor: "pointer",
    transition: "transform 0.1s ease",
  });

  return (
    <div style={container}>
      {/* Top App Header Section */}
      <div style={headerCard}>
        <h1 style={cardTitle}>Manual Shift Log & Status Manager</h1>
        <p style={cardSub}>Review checkout times, amend statuses, and track live check-in coordinate logs.</p>
      </div>

      {/* Alert Banners */}
      {error && <div style={{ background: "#fef2f2", color: "#b91c1c", padding: "14px 20px", borderRadius: 12, marginBottom: 16, fontWeight: 800, border: "1px solid #fee2e2" }}>{error}</div>}
      {successMsg && <div style={{ background: "#ecfdf5", color: "#047857", padding: "14px 20px", borderRadius: 12, marginBottom: 16, fontWeight: 800, border: "1px solid #d1fae5" }}>{successMsg}</div>}

      {/* 1. EDIT SHIFT FORM SECTION */}
      <div style={innerCard}>
        <div style={{ fontWeight: 900, color: "#111827", marginBottom: 4, fontSize: 18 }}>Shift Details Editor</div>
        <p style={{ color: "#6b7280", fontWeight: 600, fontSize: 13, marginBottom: 20, marginTop: 0 }}>
          {selectedShiftId ? `Currently Editing Shift ID: ${selectedShiftId}` : "Select a shift from the workspace table below to edit."}
        </p>

        <form onSubmit={handleUpdateShiftStatus}>
          {/* Read-Only Informational Grid Rows */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 20 }}>
            <div>
              <label style={labelStyle}>Client Name</label>
              <input type="text" value={clientName} readOnly style={disabledInput} />
            </div>
            <div>
              <label style={labelStyle}>Assigned Staff</label>
              <input type="text" value={staffName} readOnly style={disabledInput} />
            </div>
            <div>
              <label style={labelStyle}>Shift Date</label>
              <input type="text" value={shiftDate} readOnly style={disabledInput} />
            </div>
          </div>

          {/* Editable Field Interactivity Block */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            <div>
              <label style={labelStyle}>Check-In Time</label>
              <input 
                type="text" 
                placeholder="e.g. 08:00 or None" 
                value={checkInTime} 
                onChange={(e) => setCheckInTime(e.target.value)} 
                style={activeInput} 
              />
            </div>
            <div>
              <label style={labelStyle}>Check-Out Time</label>
              <input 
                type="text" 
                placeholder="e.g. 17:00 or None" 
                value={checkOutTime} 
                onChange={(e) => setCheckOutTime(e.target.value)} 
                style={activeInput} 
              />
            </div>
            <div>
              <label style={labelStyle}>Operational Status</label>
              <select 
                value={status} 
                onChange={(e) => setStatus(e.target.value)} 
                style={activeInput}
              >
                <option value="Scheduled">Scheduled</option>
                <option value="Checked In">Checked In</option>
                <option value="Checked Out">Checked Out</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button 
              type="submit" 
              disabled={loading || !selectedShiftId} 
              style={btn(loading || !selectedShiftId ? "#9ca3af" : "#10b981")}
            >
              {loading ? "Saving changes..." : "Apply & Update Shift Data"}
            </button>
          </div>
        </form>
      </div>

      {/* 2. LIVE DATABASE WORKSPACE TABLE */}
      <div style={tableWrapper}>
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #e5e7eb", background: "#ffffff" }}>
          <div style={{ fontWeight: 900, color: "#111827", fontSize: 16 }}>All System Shifts Repository</div>
          <div style={{ color: "#6b7280", fontWeight: 600, fontSize: 13, marginTop: 2 }}>Click on any table row entry below to fill the fields in the management layout above.</div>
        </div>

        <div style={scrollContainer}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Staff Member</th>
                <th style={thStyle}>Check In</th>
                <th style={thStyle}>Check Out</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>In/Out Coordinates</th>
              </tr>
            </thead>
            <tbody>
              {shifts.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ ...tdStyle, textAlign: "center", color: "#9ca3af", padding: "32px 16px" }}>
                    No shifts matching the parameters found in Firestore.
                  </td>
                </tr>
              ) : (
                shifts.map((s) => {
                  const latIn = Number(s.checkInLat);
                  const lngIn = Number(s.checkInLng);
                  const latOut = Number(s.checkOutLat);
                  const lngOut = Number(s.checkOutLng);

                  const checkInLoc = (!isNaN(latIn) && !isNaN(lngIn) && s.checkInLat && s.checkInLng) 
                    ? `${latIn.toFixed(4)}, ${lngIn.toFixed(4)}` 
                    : "";

                  const checkOutLoc = (!isNaN(latOut) && !isNaN(lngOut) && s.checkOutLat && s.checkOutLng) 
                    ? `${latOut.toFixed(4)}, ${lngOut.toFixed(4)}` 
                    : "";
                 
                  let totalLocationString = "None";
                  if (checkInLoc && checkOutLoc) totalLocationString = `In: [${checkInLoc}] | Out: [${checkOutLoc}]`;
                  else if (checkInLoc) totalLocationString = `In: [${checkInLoc}]`;
                  else if (checkOutLoc) totalLocationString = `Out: [${checkOutLoc}]`;

                  const isSelected = selectedShiftId === s.id;

                  return (
                    <tr 
                      key={s.id} 
                      onClick={() => handleSelectShift(s)} 
                      style={{ 
                        cursor: "pointer", 
                        background: isSelected ? "#eff6ff" : "transparent",
                        transition: "background 0.15s ease"
                      }}
                      onMouseEnter={(e) => !isSelected && (e.currentTarget.style.background = "#f9fafb")}
                      onMouseLeave={(e) => !isSelected && (e.currentTarget.style.background = "transparent")}
                    >
                      <td style={tdStyle}>{formatFieldValue(s.date) || "—"}</td>
                      <td style={tdStyle}>{s.clientName || s.client || "—"}</td>
                      <td style={tdStyle}>{s.staffName || s.staff || "Unassigned"}</td>
                      <td style={tdStyle}>{formatFieldValue(s.checkInTime) || "None"}</td>
                      <td style={tdStyle}>{formatFieldValue(s.checkOutTime) || "None"}</td>
                      <td style={tdStyle}>
                        <span style={{
                          display: "inline-block",
                          padding: "4px 10px",
                          borderRadius: 8,
                          fontSize: 12,
                          fontWeight: 800,
                          background: s.status === "Checked Out" ? "#d1fae5" : s.status === "Checked In" ? "#dbeafe" : "#f3f4f6",
                          color: s.status === "Checked Out" ? "#065f46" : s.status === "Checked In" ? "#1e40af" : "#374151"
                        }}>
                          {s.status || "Scheduled"}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: totalLocationString === "None" ? "#9ca3af" : "#4b5563", fontSize: 12, fontFamily: "monospace" }}>
                        {totalLocationString}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}